import React from 'react';
import { Check, Edit2, Loader, Search, X } from 'react-feather';

import { useArtist } from './hooks/useArtist';

const ArtistListPage: React.FC = () => {
  const { states, handlers } = useArtist();
  const {
    categories,
    selectedCategory,
    artists,
    editingArtist,
    editForm,
    newArtist,
    searchTerm,
    isSearching,
    isLoading,
  } = states;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader className="animate-spin text-[#8F689A]" size={32} />
      </div>
    );
  }

  return (
    <main className="flex min-h-full flex-col bg-[#F1F1F1]">
      <div className="flex min-h-full flex-1 flex-col px-10 pb-20 pt-6">
        <header className="mb-7">
          <h1 className="text-2xl font-bold">Artist</h1>
        </header>

        <div className="flex gap-5">
          <section className="w-2/3">
            <div className="flex gap-2">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  onClick={() => handlers.handleCategoryChange(category)}
                  className={`rounded-tl-[4px] rounded-tr-[4px] px-3 py-1 text-sm font-medium ${
                    selectedCategory?.id === category.id
                      ? 'bg-white text-[#8F689A]'
                      : 'bg-[#DEDEDE] text-white'
                  }`}
                >
                  {category.category}
                </button>
              ))}
            </div>

            <div className="rounded-[4px] rounded-tl-none bg-white shadow-sm">
              <div className="broder-[#DEDEDE] flex items-center border-b px-6 py-3">
                <Search size={16} className="text-[#707070]" />
                <input
                  type="text"
                  placeholder="search"
                  value={searchTerm}
                  onChange={handlers.handleSearch}
                  className="ml-2 flex-1 text-sm outline-none"
                />
              </div>

              {isSearching ? (
                <div className="flex h-screen items-center justify-center">
                  <Loader className="animate-spin text-[#8F689A]" size={24} />
                </div>
              ) : (
                <div className="custom-scrollbar h-screen space-y-2 overflow-y-scroll px-3 py-4">
                  {artists.map((artist) => (
                    <div
                      key={artist.id}
                      className="flex items-center justify-start gap-4 rounded"
                    >
                      <div className="flex gap-4">
                        <input
                          type="text"
                          value={
                            editingArtist === artist.id
                              ? editForm.nameKo
                              : artist.artistNameKO
                          }
                          onChange={(e) =>
                            editingArtist === artist.id &&
                            handlers.handleEditFormChange(
                              'nameKo',
                              e.target.value,
                            )
                          }
                          className="w-52 rounded-[4px] border border-[#CECECE] px-2 py-1 text-sm outline-none"
                          readOnly={editingArtist !== artist.id}
                        />
                        <input
                          type="text"
                          value={
                            editingArtist === artist.id
                              ? editForm.nameEn
                              : artist.artistNameEN
                          }
                          onChange={(e) =>
                            editingArtist === artist.id &&
                            handlers.handleEditFormChange(
                              'nameEn',
                              e.target.value,
                            )
                          }
                          className="w-52 rounded-[4px] border border-[#CECECE] px-2 py-1 text-sm outline-none"
                          readOnly={editingArtist !== artist.id}
                        />
                      </div>
                      <div className="flex gap-2">
                        {selectedCategory?.id !== -1 &&
                          (editingArtist === artist.id ? (
                            <button
                              type="button"
                              onClick={() => handlers.handleSaveEdit(artist.id)}
                              className="boder-[#F6F6F6] rounded-[4px] border p-1 text-[#888888] hover:bg-[#8F689A] hover:text-white"
                            >
                              <Check size={12} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handlers.handleEditClick(artist)}
                              className="boder-[#F6F6F6] rounded-[4px] border p-1 text-[#888888]"
                            >
                              <Edit2 size={12} />
                            </button>
                          ))}

                        <button
                          type="button"
                          onClick={() => handlers.handleDelete(artist.id)}
                          className="boder-[#F6F6F6] rounded-[4px] border p-1 text-[#888888]"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="w-1/3 pt-[1.625rem]">
            <div className="rounded-[4px] border border-[#EAEAEA] bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-medium">Add artist</h2>
                <button
                  type="button"
                  onClick={handlers.handleCreateArtist}
                  className="rounded-[4px] bg-[#8F689A] px-3 py-1 text-sm text-white shadow-sm hover:bg-opacity-90"
                >
                  Save
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <select
                    value={newArtist.artistCategoryId}
                    onChange={(e) =>
                      handlers.handleNewArtistChange(
                        'artistCategoryId',
                        e.target.value,
                      )
                    }
                    className="w-1/2 rounded-[4px] border border-[#E2E2E2] bg-[#DEDEDE] p-2 text-xs outline-none"
                  >
                    {categories
                      .filter((category) => category.id !== -1)
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.category}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Artist name KR"
                    value={newArtist.artistNameKO}
                    onChange={(e) =>
                      handlers.handleNewArtistChange(
                        'artistNameKO',
                        e.target.value,
                      )
                    }
                    className="w-full rounded-[4px] border border-[#CECECE] p-2 text-sm outline-none"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Artist name EN"
                    value={newArtist.artistNameEN}
                    onChange={(e) =>
                      handlers.handleNewArtistChange(
                        'artistNameEN',
                        e.target.value,
                      )
                    }
                    className="w-full rounded-[4px] border border-[#CECECE] p-2 text-sm outline-none"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default ArtistListPage;
